import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  ChevronLeft,
  CheckCircle,
  AlertCircle,
  Stethoscope,
  User,
} from 'lucide-react';
import { Button, Badge } from '@/components/ui';
import { portalService } from '@/services/portalService';
import { appointmentService } from '@/services/appointmentService';
import { resourceService } from '@/services/resourceService';
import type { SelfScheduleRequest, JourneyStepSummary } from '@/types/portal';
import type { AvailableTimeSlot } from '@/types/appointment';
import styles from './PatientSchedulingPage.module.css';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'info';

type ScheduleStep = 'procedure' | 'resource' | 'date' | 'time' | 'confirm';

const STEP_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  SCHEDULED: 'Agendado',
  COMPLETED: 'Concluído',
  SKIPPED: 'Pulado',
};

const STEP_STATUS_BADGE: Record<string, BadgeVariant> = {
  PENDING: 'neutral',
  SCHEDULED: 'info',
  COMPLETED: 'success',
  SKIPPED: 'warning',
};

interface ProcedureOption {
  id: string;
  procedureTypeId: string;
  name: string;
}

interface ResourceOption {
  id: string;
  name: string;
}

/**
 * Generates an array of date strings (YYYY-MM-DD) for the next N days.
 */
function getNextDays(count: number): string[] {
  const dates: string[] = [];
  const today = new Date();
  for (let i = 1; i <= count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

/**
 * Formats a date string (YYYY-MM-DD) to a friendly pt-BR format.
 */
function formatDateFriendly(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const weekday = date.toLocaleDateString('pt-BR', { weekday: 'short' });
  const day = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${weekday}, ${day}`;
}

/**
 * Patient self-scheduling page.
 *
 * Supports two modes:
 * 1. Campaign journey mode: shows journey steps and which ones
 *    are available to schedule
 * 2. Standalone mode: procedure type -> resource -> date -> time slot
 *
 * Mobile-first design with step-by-step flow.
 * Uses available time slots from the schedule service API.
 * Includes confirmation step before booking.
 */
export function PatientSchedulingPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<ScheduleStep>('procedure');
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureOption | null>(null);
  const [selectedResource, setSelectedResource] = useState<ResourceOption | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlot | null>(null);
  const [isBooked, setIsBooked] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  // Fetch portal data to check for active journey (backend resolves patient from JWT)
  const { data: portal } = useQuery({
    queryKey: ['portal', 'my-portal'],
    queryFn: () => portalService.getMyPortal(),
  });

  const hasJourney = Boolean(portal?.activeJourney);
  const journeySteps = portal?.activeJourney?.steps ?? [];
  const pendingSteps = journeySteps.filter((s) => s.status === 'PENDING');

  // Fetch active resources for resource selection step
  const {
    data: resources = [],
    isLoading: isResourcesLoading,
  } = useQuery({
    queryKey: ['resources', 'active'],
    queryFn: () => resourceService.getResources({ isActive: true }),
    enabled: currentStep === 'resource',
  });

  // Fetch available time slots when resource + date + procedure are selected
  const {
    data: availableSlots = [],
    isLoading: isSlotsLoading,
  } = useQuery({
    queryKey: [
      'available-slots',
      selectedResource?.id,
      selectedDate,
      selectedProcedure?.procedureTypeId,
    ],
    queryFn: () =>
      appointmentService.getAvailableSlots(
        selectedResource?.id ?? '',
        selectedDate ?? '',
        selectedProcedure?.procedureTypeId ?? '',
      ),
    enabled: Boolean(selectedResource?.id && selectedDate && selectedProcedure?.procedureTypeId),
  });

  const scheduleMutation = useMutation({
    mutationFn: (data: SelfScheduleRequest) => portalService.selfSchedule(data),
    onSuccess: () => {
      setIsBooked(true);
      setBookingError(null);
      queryClient.invalidateQueries({ queryKey: ['portal'] });
    },
    onError: () => {
      setBookingError('Erro ao agendar. Tente novamente ou escolha outro horário.');
    },
  });

  const availableDates = getNextDays(14);

  const handleSelectJourneyStep = useCallback((step: JourneyStepSummary) => {
    setSelectedProcedure({
      id: step.stepId,
      procedureTypeId: step.procedureTypeId,
      name: step.procedureTypeName,
    });
    setCurrentStep('resource');
  }, []);

  const handleSelectResource = useCallback((resource: ResourceOption) => {
    setSelectedResource(resource);
    setCurrentStep('date');
  }, []);

  const handleSelectDate = useCallback((date: string) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setCurrentStep('time');
  }, []);

  const handleSelectSlot = useCallback((slot: AvailableTimeSlot) => {
    setSelectedSlot(slot);
    setCurrentStep('confirm');
  }, []);

  const handleBack = useCallback(() => {
    switch (currentStep) {
      case 'resource':
        setCurrentStep('procedure');
        setSelectedResource(null);
        break;
      case 'date':
        setCurrentStep('resource');
        setSelectedDate(null);
        break;
      case 'time':
        setCurrentStep('date');
        setSelectedSlot(null);
        break;
      case 'confirm':
        setCurrentStep('time');
        break;
    }
  }, [currentStep]);

  const handleConfirm = useCallback(() => {
    if (!selectedProcedure || !selectedResource || !selectedDate || !selectedSlot) {
      return;
    }

    const payload: SelfScheduleRequest = {
      patientId: portal?.patientId ?? '',
      resourceId: selectedResource.id,
      procedureTypeId: selectedProcedure.procedureTypeId,
      date: selectedDate,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      campaignId: portal?.activeJourney?.campaignId,
      journeyId: portal?.activeJourney?.journeyId,
    };

    scheduleMutation.mutate(payload);
  }, [
    portal,
    selectedProcedure,
    selectedResource,
    selectedDate,
    selectedSlot,
    scheduleMutation,
  ]);

  const handleNewBooking = useCallback(() => {
    setCurrentStep('procedure');
    setSelectedProcedure(null);
    setSelectedResource(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setIsBooked(false);
    setBookingError(null);
  }, []);

  // Success state
  if (isBooked) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successState} role="status">
            <CheckCircle size={48} className={styles.successIcon} aria-hidden="true" />
            <h1 className={styles.successTitle}>Agendamento confirmado!</h1>
            <p className={styles.successMessage}>
              Seu procedimento <strong>{selectedProcedure?.name}</strong> foi agendado
              para <strong>{selectedDate ? formatDateFriendly(selectedDate) : ''}</strong> das{' '}
              <strong>{selectedSlot?.startTime}</strong> as{' '}
              <strong>{selectedSlot?.endTime}</strong>.
            </p>
            <div className={styles.successActions}>
              <Button onClick={handleNewBooking} fullWidth>
                Agendar outro procedimento
              </Button>
              <Button variant="outline" onClick={() => navigate('/portal/dashboard')} fullWidth>
                Voltar ao painel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <header className={styles.header}>
          <div className={styles.logo}>
            <span className={styles.logoIcon} aria-hidden="true">+</span>
            <span className={styles.logoText}>
              CRM<span className={styles.logoAccent}>Clinica</span>
            </span>
          </div>
          <h1 className={styles.title}>Agendar Procedimento</h1>
        </header>

        {/* Step indicator */}
        <nav className={styles.stepIndicator} aria-label="Etapas do agendamento">
          <ol className={styles.stepList}>
            <li className={currentStep === 'procedure' ? styles.stepActive : styles.stepDone}>
              <span className={styles.stepNumber}>1</span>
              <span className={styles.stepLabel}>Procedimento</span>
            </li>
            <li
              className={
                currentStep === 'resource'
                  ? styles.stepActive
                  : selectedResource
                    ? styles.stepDone
                    : styles.stepPending
              }
            >
              <span className={styles.stepNumber}>2</span>
              <span className={styles.stepLabel}>Profissional</span>
            </li>
            <li
              className={
                currentStep === 'date'
                  ? styles.stepActive
                  : selectedDate
                    ? styles.stepDone
                    : styles.stepPending
              }
            >
              <span className={styles.stepNumber}>3</span>
              <span className={styles.stepLabel}>Data</span>
            </li>
            <li
              className={
                currentStep === 'time'
                  ? styles.stepActive
                  : selectedSlot
                    ? styles.stepDone
                    : styles.stepPending
              }
            >
              <span className={styles.stepNumber}>4</span>
              <span className={styles.stepLabel}>Horário</span>
            </li>
            <li className={currentStep === 'confirm' ? styles.stepActive : styles.stepPending}>
              <span className={styles.stepNumber}>5</span>
              <span className={styles.stepLabel}>Confirmar</span>
            </li>
          </ol>
        </nav>

        {/* Step 1: Procedure / Journey Steps */}
        {currentStep === 'procedure' && (
          <section className={styles.stepContent} aria-label="Selecionar procedimento">
            {hasJourney && pendingSteps.length > 0 ? (
              <>
                <h2 className={styles.sectionTitle}>
                  Sua jornada: {portal?.activeJourney?.campaignName}
                </h2>
                <p className={styles.sectionHint}>
                  Selecione o procedimento que deseja agendar.
                </p>

                <ul className={styles.optionList} role="list">
                  {journeySteps.map((step) => {
                    const isPending = step.status === 'PENDING';
                    const isScheduled = step.status === 'SCHEDULED';
                    const isCompleted = step.status === 'COMPLETED';

                    return (
                      <li key={step.stepId}>
                        <button
                          type="button"
                          className={[
                            styles.optionCard,
                            !isPending ? styles.optionDisabled : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => isPending && handleSelectJourneyStep(step)}
                          disabled={!isPending}
                          aria-label={`${step.procedureTypeName} - ${STEP_STATUS_LABELS[step.status] ?? step.status}`}
                        >
                          <div className={styles.optionInfo}>
                            <span className={styles.optionOrder}>{step.orderIndex + 1}.</span>
                            <span className={styles.optionName}>{step.procedureTypeName}</span>
                          </div>
                          <Badge variant={STEP_STATUS_BADGE[step.status] ?? 'neutral'}>
                            {STEP_STATUS_LABELS[step.status] ?? step.status}
                          </Badge>
                          {isScheduled && step.scheduledAt && (
                            <span className={styles.scheduledInfo}>
                              Agendado: {new Date(step.scheduledAt).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                          {isCompleted && (
                            <CheckCircle size={16} className={styles.completedIcon} aria-hidden="true" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {pendingSteps.length === 0 && (
                  <div className={styles.policyMessage} role="status">
                    <AlertCircle size={18} aria-hidden="true" />
                    <span>Todos os procedimentos da jornada já foram agendados ou concluídos.</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className={styles.sectionTitle}>Selecione o procedimento</h2>
                <p className={styles.sectionHint}>
                  Escolha o tipo de procedimento que deseja agendar.
                </p>

                {/* In production, this would fetch procedure types from API */}
                <div className={styles.emptyState} role="status">
                  <Stethoscope size={32} className={styles.emptyIcon} aria-hidden="true" />
                  <p>
                    Para agendar, entre em contato com a clínica ou acesse
                    através de um convite.
                  </p>
                </div>
              </>
            )}
          </section>
        )}

        {/* Step 2: Resource selection */}
        {currentStep === 'resource' && (
          <section className={styles.stepContent} aria-label="Selecionar profissional">
            <div className={styles.stepHeader}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBack}
                aria-label="Voltar para seleção de procedimento"
              >
                <ChevronLeft size={20} aria-hidden="true" />
                <span>Voltar</span>
              </button>
            </div>

            <h2 className={styles.sectionTitle}>Selecione o profissional</h2>
            <p className={styles.sectionHint}>
              Procedimento: <strong>{selectedProcedure?.name}</strong>
            </p>

            {isResourcesLoading ? (
              <div className={styles.loadingState} role="status" aria-busy="true">
                <p>Carregando profissionais disponíveis...</p>
              </div>
            ) : resources.length === 0 ? (
              <div className={styles.emptyState} role="status">
                <User size={32} className={styles.emptyIcon} aria-hidden="true" />
                <p>Nenhum profissional disponível no momento.</p>
              </div>
            ) : (
              <ul className={styles.optionList} role="list">
                {resources.map((resource) => (
                  <li key={resource.id}>
                    <button
                      type="button"
                      className={styles.optionCard}
                      onClick={() =>
                        handleSelectResource({ id: resource.id, name: resource.name })
                      }
                      aria-label={resource.name}
                    >
                      <div className={styles.optionInfo}>
                        <User size={18} aria-hidden="true" />
                        <span className={styles.optionName}>{resource.name}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Step 3: Date selection */}
        {currentStep === 'date' && (
          <section className={styles.stepContent} aria-label="Selecionar data">
            <div className={styles.stepHeader}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBack}
                aria-label="Voltar para seleção de profissional"
              >
                <ChevronLeft size={20} aria-hidden="true" />
                <span>Voltar</span>
              </button>
            </div>

            <h2 className={styles.sectionTitle}>Selecione a data</h2>
            <p className={styles.sectionHint}>
              Profissional: <strong>{selectedResource?.name}</strong>
            </p>

            <ul className={styles.dateGrid} role="list">
              {availableDates.map((date) => (
                <li key={date}>
                  <button
                    type="button"
                    className={[
                      styles.dateCard,
                      selectedDate === date ? styles.dateSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleSelectDate(date)}
                    aria-label={formatDateFriendly(date)}
                    aria-pressed={selectedDate === date}
                  >
                    <Calendar size={16} aria-hidden="true" />
                    <span>{formatDateFriendly(date)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Step 4: Time slot selection */}
        {currentStep === 'time' && (
          <section className={styles.stepContent} aria-label="Selecionar horário">
            <div className={styles.stepHeader}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBack}
                aria-label="Voltar para seleção de data"
              >
                <ChevronLeft size={20} aria-hidden="true" />
                <span>Voltar</span>
              </button>
            </div>

            <h2 className={styles.sectionTitle}>Selecione o horário</h2>
            <p className={styles.sectionHint}>
              Data: <strong>{selectedDate ? formatDateFriendly(selectedDate) : ''}</strong>
            </p>

            {isSlotsLoading ? (
              <div className={styles.loadingState} role="status" aria-busy="true">
                <p>Carregando horários disponíveis...</p>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className={styles.emptyState} role="status">
                <Clock size={32} className={styles.emptyIcon} aria-hidden="true" />
                <p>Nenhum horário disponível para esta data. Escolha outra data.</p>
                <Button variant="outline" onClick={handleBack}>
                  Voltar e escolher outra data
                </Button>
              </div>
            ) : (
              <ul className={styles.slotGrid} role="list">
                {availableSlots.map((slot) => (
                  <li key={`${slot.startTime}-${slot.endTime}`}>
                    <button
                      type="button"
                      className={[
                        styles.slotCard,
                        selectedSlot?.startTime === slot.startTime
                          ? styles.slotSelected
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleSelectSlot(slot)}
                      aria-label={`${slot.startTime} as ${slot.endTime}`}
                      aria-pressed={selectedSlot?.startTime === slot.startTime}
                    >
                      <Clock size={14} aria-hidden="true" />
                      <span>
                        {slot.startTime} - {slot.endTime}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Step 5: Confirmation */}
        {currentStep === 'confirm' && (
          <section className={styles.stepContent} aria-label="Confirmar agendamento">
            <div className={styles.stepHeader}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBack}
                aria-label="Voltar para seleção de horário"
              >
                <ChevronLeft size={20} aria-hidden="true" />
                <span>Voltar</span>
              </button>
            </div>

            <h2 className={styles.sectionTitle}>Confirmar agendamento</h2>

            <div className={styles.confirmSummary}>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Procedimento</span>
                <span className={styles.confirmValue}>{selectedProcedure?.name}</span>
              </div>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Profissional</span>
                <span className={styles.confirmValue}>{selectedResource?.name}</span>
              </div>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Data</span>
                <span className={styles.confirmValue}>
                  {selectedDate ? formatDateFriendly(selectedDate) : ''}
                </span>
              </div>
              <div className={styles.confirmRow}>
                <span className={styles.confirmLabel}>Horário</span>
                <span className={styles.confirmValue}>
                  {selectedSlot?.startTime} - {selectedSlot?.endTime}
                </span>
              </div>
            </div>

            {bookingError && (
              <p className={styles.formError} role="alert">
                {bookingError}
              </p>
            )}

            <Button
              fullWidth
              onClick={handleConfirm}
              isLoading={scheduleMutation.isPending}
            >
              Confirmar agendamento
            </Button>
          </section>
        )}
      </div>
    </div>
  );
}
